/*!
 * KODIAK SPORTS LLC — PROPRIETARY SOFTWARE
 * Copyright 2026 Kodiak Sports, LLC. All rights reserved.
 *
 * kodiak-rubber-quote-engine.js  v1.1.0
 * Pure calculation engine for the Kodiak Rolled Rubber Flooring Calculator.
 * NO DOM ACCESS in this file. All functions are pure: same input -> same output.
 * Consumed by assets/kodiak-rolled-rubber-calculator.js (DOM glue) and available
 * to agents/integrations as window.KodiakRubberQuote.
 *
 * v1.1.0 (2026-09-28)
 *  - Enforces per-product max_roll_length_ft. A strip longer than the maximum is
 *    split into equal rolls (each >= min cut, <= max). Never emits an over-length roll.
 *  - Pricing output uses explicit, unambiguous fields (material only):
 *    material_subtotal, estimated_volume_discount, material_after_discount_estimate.
 *    Freight and tax are NOT included; volume discount is confirmed at checkout.
 *
 * UNAUTHORIZED COPYING, REPRODUCTION, MODIFICATION, DISTRIBUTION, OR USE
 * OF THIS CODE OR ITS DESIGN — IN WHOLE OR IN PART — IS STRICTLY PROHIBITED
 * WITHOUT THE EXPRESS WRITTEN CONSENT OF KODIAK SPORTS, LLC.
 *
 * Kodiak Sports, LLC | www.kodiaksports.com
 */
(function (root, factory) {
  "use strict";
  var api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  if (root) root.KodiakRubberQuote = api;
})(typeof window !== "undefined" ? window : this, function () {
  "use strict";

  var DEFAULTS = { roll_width_ft: 4, min_roll_length_ft: 15, max_roll_length_ft: 0, order_min_usd: 500 };

  function parseFeet(raw) {
    if (raw == null || raw === "") return 0;
    raw = raw.toString().trim();
    var fi = raw.match(/^(\d+(?:\.\d+)?)['\s\-](\d+(?:\.\d+)?)(?:\")?$/);
    if (fi) return parseFloat(fi[1]) + parseFloat(fi[2]) / 12;
    var wf = raw.match(/^(\d+)\s+(\d+)\/(\d+)$/);
    if (wf) return parseInt(wf[1], 10) + parseInt(wf[2], 10) / parseInt(wf[3], 10);
    var fo = raw.match(/^(\d+)\/(\d+)$/);
    if (fo) return parseInt(fo[1], 10) / parseInt(fo[2], 10);
    var n = parseFloat(raw);
    return isNaN(n) ? 0 : n;
  }

  function round2(n) { return Math.round(n * 100) / 100; }
  function round1(n) { return Math.round(n * 10) / 10; }

  /**
   * One layout option for one area.
   * direction "length": strips run along room length (strips = ceil(width / roll width)).
   * direction "width":  strips run along room width  (strips = ceil(length / roll width)).
   * max_roll_length_ft (optional, 0 = unlimited): strips longer than this are split
   * into `pieces` equal rolls, each within [min, max].
   */
  function calcRollOption(room_length_ft, room_width_ft, roll_width_ft, direction, min_roll_length_ft, max_roll_length_ft) {
    if (room_length_ft <= 0 || room_width_ft <= 0 || roll_width_ft <= 0) return null;
    var minLen = min_roll_length_ft > 0 ? min_roll_length_ft : DEFAULTS.min_roll_length_ft;
    var maxLen = max_roll_length_ft > 0 ? max_roll_length_ft : 0;

    var strips, run_ft;
    if (direction === "length") {
      strips = Math.ceil(room_width_ft / roll_width_ft);
      run_ft = Math.max(Math.ceil(room_length_ft), minLen);
    } else {
      strips = Math.ceil(room_length_ft / roll_width_ft);
      run_ft = Math.max(Math.ceil(room_width_ft), minLen);
    }

    var pieces = 1, roll_length_ft = run_ft;
    if (maxLen > 0 && run_ft > maxLen) {
      pieces = Math.ceil(run_ft / maxLen);
      roll_length_ft = Math.max(Math.ceil(run_ft / pieces), minLen);
    }

    var num_rolls = strips * pieces;
    var purchased_sqft = num_rolls * roll_width_ft * roll_length_ft;
    var split = pieces > 1;
    var splitNote = split ? " (runs split: max " + maxLen + "' per roll)" : "";

    return {
      direction: direction,
      num_rolls: num_rolls,
      strips: strips,
      pieces_per_strip: pieces,
      split_for_max_length: split,
      max_roll_length_ft: maxLen || null,
      roll_length_ft: roll_length_ft,
      roll_width_ft: roll_width_ft,
      purchased_sqft: purchased_sqft,
      display_note: num_rolls + " - " + roll_width_ft + "' x " + roll_length_ft + "' Rolls = " + purchased_sqft.toLocaleString() + " sq ft" + splitNote,
      cart_note: num_rolls + " - " + roll_width_ft + "' x " + roll_length_ft + "' Rolls, Kit Size: " + purchased_sqft + " Sq. Ft" + splitNote
    };
  }

  function resolveDiscountTier(subtotal_usd, tiers) {
    var pct = 0, min_cents = 0, cents = Math.round(subtotal_usd * 100);
    (tiers || []).forEach(function (t) {
      var tMin = (t.min !== undefined ? t.min : t.min_cents) || 0;
      if (cents >= tMin && t.pct >= pct) { pct = t.pct; min_cents = tMin; }
    });
    return { pct: pct, min_cents: min_cents };
  }

  function computeRubberQuote(input) {
    input = input || {};
    var rollWidth = input.roll_width_ft > 0 ? input.roll_width_ft : DEFAULTS.roll_width_ft;
    var minLen = input.min_roll_length_ft > 0 ? input.min_roll_length_ft : DEFAULTS.min_roll_length_ft;
    var maxLen = input.max_roll_length_ft > 0 ? input.max_roll_length_ft : 0;
    var areasIn = Array.isArray(input.areas) ? input.areas : [];
    var areasOut = [], totalRoom = 0, totalPurchased = 0, totalRolls = 0, anySplit = false;

    areasIn.forEach(function (a, idx) {
      a = a || {};
      var L = typeof a.room_length_ft === "number" ? a.room_length_ft : parseFeet(a.room_length_ft);
      var W = typeof a.room_width_ft === "number" ? a.room_width_ft : parseFeet(a.room_width_ft);
      var areaNumber = a.area_number || idx + 1;
      if (L <= 0 || W <= 0) {
        areasOut.push({ area_number: areaNumber, room_length_ft: L, room_width_ft: W, room_sqft: 0,
          options: { length_wise: null, width_wise: null }, best_direction: null, selected_direction: null,
          selected: null, purchased_sqft: 0, waste_sqft: 0, waste_pct: 0 });
        return;
      }
      var roomSqft = L * W;
      var opt1 = calcRollOption(L, W, rollWidth, "length", minLen, maxLen);
      var opt2 = calcRollOption(L, W, rollWidth, "width", minLen, maxLen);
      // Lowest purchased sq ft wins; on a tie prefer the layout that needs no split, then length-wise.
      var bestDir;
      if (opt2.purchased_sqft < opt1.purchased_sqft) bestDir = "width";
      else if (opt2.purchased_sqft > opt1.purchased_sqft) bestDir = "length";
      else bestDir = (opt1.split_for_max_length && !opt2.split_for_max_length) ? "width" : "length";
      var requested = a.roll_direction;
      var selectedDir = requested === "length" || requested === "width" ? requested : bestDir;
      var selected = selectedDir === "length" ? opt1 : opt2;
      if (selected.split_for_max_length) anySplit = true;
      totalRoom += roomSqft;
      totalPurchased += selected.purchased_sqft;
      totalRolls += selected.num_rolls;
      areasOut.push({
        area_number: areaNumber, room_length_ft: round2(L), room_width_ft: round2(W), room_sqft: round2(roomSqft),
        options: { length_wise: opt1, width_wise: opt2 }, best_direction: bestDir, selected_direction: selectedDir,
        selected: selected, purchased_sqft: selected.purchased_sqft,
        waste_sqft: round2(selected.purchased_sqft - roomSqft),
        waste_pct: roomSqft > 0 ? round1((selected.purchased_sqft - roomSqft) / roomSqft * 100) : 0
      });
    });

    var totals = {
      room_sqft: round2(totalRoom), purchased_sqft: totalPurchased,
      waste_sqft: round2(totalPurchased - totalRoom),
      waste_pct: totalRoom > 0 ? round1((totalPurchased - totalRoom) / totalRoom * 100) : 0,
      num_rolls: totalRolls, max_roll_length_ft: maxLen || null, split_for_max_length: anySplit
    };

    var pricing = null;
    if (typeof input.price_per_sqft === "number" && input.price_per_sqft > 0 && totalPurchased > 0) {
      var subtotal = round2(totalPurchased * input.price_per_sqft);
      var tier = resolveDiscountTier(subtotal, input.discount_tiers);
      var discountAmount = round2(subtotal * tier.pct / 100);
      var orderMin = input.order_min_usd > 0 ? input.order_min_usd : DEFAULTS.order_min_usd;
      pricing = {
        currency: "USD",
        pricing_basis: "per_square_foot",
        quantity_basis: "square_feet",
        price_per_sqft: input.price_per_sqft,
        purchased_sqft: totalPurchased,
        material_subtotal: subtotal,
        volume_discount_pct: tier.pct,
        estimated_volume_discount: discountAmount,
        material_after_discount_estimate: round2(subtotal - discountAmount),
        discount_confirmed_at: "checkout",
        discount_note: "Estimate based on this line's material subtotal. The automatic discount calculated at checkout is authoritative.",
        freight: null,
        tax: null,
        excludes: ["freight", "tax"],
        total_type: "material_only",
        order_min_usd: orderMin,
        order_min_basis: "material_subtotal_before_discount",
        meets_order_min: subtotal >= orderMin
      };
    }
    return { areas: areasOut, totals: totals, pricing: pricing };
  }

  return {
    VERSION: "1.1.0",
    DEFAULTS: DEFAULTS,
    parseFeet: parseFeet,
    calcRollOption: calcRollOption,
    resolveDiscountTier: resolveDiscountTier,
    computeRubberQuote: computeRubberQuote
  };
});
